import React from 'react';
import { Link } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';

import { getBenefitInfo, getLocalURI, isExternalUrl } from '@share/utils';
import { BenefitsEnum, IBenefits, IBenefitsItems, IAccount } from '@share/common-types';
import { SkeletonItems } from '@components';

import './style.scss';
import { get, isEmpty } from 'lodash';

export interface ICruisesProgramListProps {
  forceMessages?: boolean;
  desc3Style?: React.CSSProperties;
  account?: IAccount | null;
  benefits: IBenefits | null | undefined;
}

const ZERO = 0;

function renderProgramContent(
  item: IBenefitsItems,
  benefitsCount: number,
  account: IAccount | null | undefined,
  desc3Style: React.CSSProperties | undefined,
) {
  const backgroundColorPreference = account?.buttonColor;
  const backgroundColor = !isEmpty(backgroundColorPreference) ? backgroundColorPreference : 'rgb(46, 45, 235)';

  return (
    <>
      <div className={`cruises-program-list__image-wrapper ${benefitsCount === 1 ? 'single' : ''}`}>
        <img src={item.imageUrl} alt={item.title} />
      </div>
      <div className="cruises-program-list__info">
        <p className="cruises-program-list__info-title">{item.title}</p>
        <p className="cruises-program-list__info-content">
          <FormattedMessage id={item.description} />
        </p>

        <p className="cruises-program-list__info-content">
          <FormattedMessage id={item.description2} />
        </p>

        {item.description3 ? (
          <p className="cruises-program-list__info-content" style={desc3Style}>
            <FormattedMessage id={item.description3} />
          </p>
        ) : null}

        {item.description4 ? (
          <p className="cruises-program-list__info-content">
            <FormattedMessage id={item.description4} />
          </p>
        ) : null}

        {item.description5 ? (
          <p className="cruises-program-list__info-content">
            <FormattedMessage id={item.description5} />
          </p>
        ) : null}

        {item.errorMessage ? (
          <p className="cruises-program-list__info-content centered error">
            <FormattedMessage id={item.errorMessage} />
          </p>
        ) : null}

        <div className="cruises-program-list__info-button-container">
          <div className="cruises-program-list__info-button" style={{ backgroundColor }}>
            {item?.buttonMessage ? item.buttonMessage : <FormattedMessage id="check.out.sailings" />}
          </div>
        </div>
      </div>
    </>
  );
}

export function CruisesProgramList({
  forceMessages,
  desc3Style,
  account,
  benefits,
}: ICruisesProgramListProps): React.ReactElement {
  const cruises: IBenefitsItems[] = benefits?.items
    ? get(getBenefitInfo(benefits, BenefitsEnum.Cruises), `[${ZERO}].childItems`, [])
    : [];
  const cruisesFiltered = cruises.filter(cruise => cruise.title === 'Luxury Cruises');
  const {
    url,
    imageUrl,
    titleForce,
    buttonMessage,
    description,
    description2,
    description3,
    description4,
    description5,
    errorMessage,
  } = get(cruisesFiltered, '[0]', {} as IBenefitsItems);
  const luxuryCruises: IBenefitsItems = {
    type: 'cruises',
    url: forceMessages ? url ?? '' : url ? url : 'https://membergetaways.com/odtest1.php?id=1620216',
    imageUrl: imageUrl ? imageUrl : 'https://rsistaticdata.s3.amazonaws.com/benefits/cruises-luxury-cruises.jpg',
    title: forceMessages ? (titleForce ?? '') : 'Luxury Ocean & River Cruises',
    description: forceMessages ? description : 'luxury.cruises.message',
    description2: forceMessages ? description2 : 'luxury.cruises.message2',
    description3,
    description4,
    description5,
    buttonMessage,
    errorMessage,
  };
  const cruisesToDisplay = [luxuryCruises];
  const linkClassNames = `cruises-program-list__item ${cruisesToDisplay.length === 1 ? 'single' : ''}`;

  return (
    <div className="cruises-program-list">
      <h1 className="cruises-program-list__header secondary-font">
        <FormattedMessage id="members.have.custom.getaway.programs" />
      </h1>
      {benefits?.items ? (
        <div className="cruises-program-list__items-wrapper">
          {cruisesToDisplay?.map((item, index) =>
            item.url && isExternalUrl(item.url) ? (
              <a
                href={item.url}
                target="_blank"
                rel="noreferrer"
                key={`${index}${item.title}`}
                className={linkClassNames}
              >
                {renderProgramContent(item, cruisesToDisplay.length, account, desc3Style)}
              </a>
            ) : (
              <Link
                to={`/${account?.name}${getLocalURI(item.url)}`}
                key={`${index}${item.title}`}
                className={linkClassNames}
              >
                {renderProgramContent(item, cruisesToDisplay.length, account, desc3Style)}
              </Link>
            ),
          )}
        </div>
      ) : (
        <SkeletonItems singleBlock={true} />
      )}
    </div>
  );
}
